import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Settings2, X } from 'lucide-react';
import Workbench from '@/pages/Workbench';
import {
  AUSWAHL_DENNIS,
  EntwurfProvider,
  STANDARD,
  VORSCHLAG,
  type EntwurfOptionen,
} from '@/entwurf/EntwurfOptionen';

/**
 * Der Werkbank-Umbau als schaltbarer Entwurf, in der echten App.
 *
 * Diese Seite baut NICHTS nach. Sie rendert `Workbench` - dieselbe Datei, die
 * unter /workbench laeuft - und legt nur andere Optionen darueber. Was hier zu
 * sehen ist, ist damit per Konstruktion umsetzbar: es IST die Umsetzung, nur
 * hinter einem Schalter.
 *
 * Genau das ist der Unterschied zu einem Mockup. Ein Mockup kann behaupten,
 * dass die einzeilige Kopfzone passt; diese Seite kann es nicht behaupten,
 * sondern nur zeigen - mit den echten Produkttiteln des Nutzers, in seiner
 * Fensterbreite, mit seiner Seitenleiste, in seinem Thema.
 *
 * Die Schalterleiste schwebt bewusst ueber der Seite statt in ihr zu stehen.
 * Die Werkbank ist eine Fluid-Route: ihre Hoehe haengt an einer Kette von
 * `min-h-0`/`flex-1` von `<main>` bis zur Karte. Ein zusaetzlicher Block im
 * Fluss wuerde genau die Hoehe verbrauchen, ueber die hier entschieden wird,
 * und die Messung verfaelschen.
 */

const SPEICHER = 'entwurf-werkbank-optionen';

/**
 * Die aktuelle Auswahl als kopierbarer Text: erst die Abweichungen vom
 * heutigen Verhalten (das ist die eigentliche Aussage), darunter der volle
 * JSON-Stand fuer die maschinelle Uebernahme. Reine Funktion, damit der
 * Inhalt testbar ist, ohne die Zwischenablage zu mocken.
 */
export function exportOptionen(optionen: EntwurfOptionen): string {
  const abweichungen = (Object.keys(STANDARD) as Array<keyof EntwurfOptionen>)
    .filter((feld) => optionen[feld] !== STANDARD[feld])
    .map((feld) => `- ${String(feld)}: ${String(optionen[feld])}`);
  const kopf =
    abweichungen.length === 0
      ? 'Entwurf-Auswahl: keine Abweichungen, alles wie heute.'
      : 'Entwurf-Auswahl, ' +
        abweichungen.length +
        ' Abweichung(en) vom heutigen Verhalten:\n' +
        abweichungen.join('\n');
  return kopf + '\n\nVollstaendig: ' + JSON.stringify(optionen);
}

type Schalter<K extends keyof EntwurfOptionen> = {
  feld: K;
  frage: string;
  hinweis?: string;
  werte: Array<{ wert: EntwurfOptionen[K]; text: string; empfohlen?: boolean }>;
};

/* eslint-disable @typescript-eslint/no-explicit-any -- Die Liste ist bewusst
   heterogen: jeder Eintrag bindet ein anderes Feld mit eigenem Wertetyp. Ein
   generischer Union-Typ ueber alle Felder waere hier mehr Zeremonie als
   Erkenntnis, und die Zuweisung unten ist durch `feld` bereits gebunden. */
export const SCHALTER: Array<Schalter<any>> = [
  {
    feld: 'kopfzone',
    frage: 'Kopfzone',
    hinweis: 'Der eigentliche Umbau. Bringt gemessen 145 px an die Karte.',
    werte: [
      { wert: 'klassisch', text: 'Wie heute' },
      { wert: 'eineZeile', text: 'Eine Zeile', empfohlen: true },
    ],
  },
  {
    feld: 'fortschritt',
    frage: 'Fortschrittszeile',
    hinweis: 'Die Position steht ohnehin schon rechts im Kartenkopf. Spart 52 px.',
    werte: [
      { wert: 'oben', text: 'Eigene Zeile' },
      { wert: 'imKartenkopf', text: 'In den Kartenkopf', empfohlen: true },
    ],
  },
  {
    feld: 'reiterStil',
    frage: 'Statusreiter',
    hinweis: 'Der Unterstrich braucht eine Grundlinie über die volle Breite. In der Zeile endet sie nach 335 px.',
    werte: [
      { wert: 'unterstrich', text: 'Unterstrich' },
      { wert: 'segmente', text: 'Segmentgruppe', empfohlen: true },
    ],
  },
  {
    feld: 'listenKachel',
    frage: 'Produktliste',
    hinweis: 'Heute teilt sie sich die Fläche mit der Karte, getrennt nur durch einen Strich.',
    werte: [
      { wert: 'inKarte', text: 'In der Karte' },
      { wert: 'eigeneKachel', text: 'Eigene Kachel', empfohlen: true },
    ],
  },
  {
    feld: 'statusAnzeige',
    frage: 'Auslieferungsstand in der Liste',
    hinweis: 'Die lange Pille misst 144 px. Dem Titel bleiben dann 29 von 300.',
    werte: [
      { wert: 'pille', text: 'Klartext-Pille' },
      { wert: 'marker', text: 'Punkt in der Markenzeile', empfohlen: true },
      { wert: 'keine', text: 'Gar nichts (wie im Mockup)' },
    ],
  },
  {
    feld: 'uploadBeschriftung',
    frage: 'Upload-Knopf',
    hinweis: 'Rund 130 px Unterschied. Entscheidet, ob die Zeile bei offener Seitenleiste umbricht.',
    werte: [
      { wert: 'lang', text: '18 freigegebene Produkte …' },
      { wert: 'kurz', text: '18 zu Google übertragen', empfohlen: true },
    ],
  },
  {
    feld: 'seitentitel',
    frage: 'Seitentitel',
    hinweis: 'Die Brotkrume trägt ihn ab 768 px wortgleich. Darunter nicht.',
    werte: [
      { wert: 'sichtbar', text: 'Immer sichtbar' },
      { wert: 'nurSchmal', text: 'Nur unter 768 px', empfohlen: true },
    ],
  },
  {
    feld: 'leistenBreite',
    frage: 'Breite der Produktliste',
    hinweis: 'Die 40 px gehen vollständig der Feed-Spalte ab.',
    werte: [
      { wert: 300, text: '300 px', empfohlen: true },
      { wert: 340, text: '340 px' },
    ],
  },
  {
    feld: 'beschreibungEditor',
    frage: 'Beschreibung im Aufklapper',
    hinweis: 'Heute stehen Diff und Textarea gleichzeitig da. 186 px, sobald jemand aufklappt.',
    werte: [
      { wert: 'wieHeute', text: 'Diff und Feld' },
      { wert: 'hinterAnpassen', text: 'Feld hinter Anpassen', empfohlen: true },
    ],
  },
  {
    feld: 'kachelVorherTitel',
    frage: 'Alter Titel in der Kachel',
    hinweis: 'Derselbe Text steht im Kartenkopf und im Diff. 14 px, eine von sieben Titelstellen.',
    werte: [
      { wert: 'zeigen', text: 'Durchgestrichen zeigen' },
      { wert: 'weglassen', text: 'Weglassen', empfohlen: true },
    ],
  },
  {
    feld: 'kopftitelMarke',
    frage: 'Titel im Kartenkopf',
    hinweis: 'Kopf und Leiste zeigen seit T3 denselben optimierten Titel. Die Marke sagt, welcher Stand das ist. 0 px.',
    werte: [
      { wert: 'ohne', text: 'Unbeschriftet' },
      { wert: 'mitOptimiert', text: 'Marke „Optimiert"', empfohlen: true },
    ],
  },
  {
    feld: 'kuerzungsHinweisLinks',
    frage: 'Zeile unter der Kachel',
    hinweis: 'Heute zwei Zeilen allgemeine Belehrung. Die Zahl sagt dasselbe ueber dieses Produkt. 12 px.',
    werte: [
      { wert: 'satz', text: 'Allgemeiner Satz' },
      { wert: 'gemesseneZahl', text: 'Gemessene Zahl', empfohlen: true },
    ],
  },
  {
    feld: 'kuerzungsAnzeige',
    frage: 'Kuerzungsmarke',
    hinweis: 'Die Kachel links zeigt den Schnitt schon, samt Zeichenzahl. Der Strich sagt es ein drittes Mal.',
    werte: [
      { wert: 'eigenerBlock', text: 'Eigener Kasten' },
      { wert: 'imNachherBlock', text: 'Strich im Diff' },
      { wert: 'keine', text: 'Keine, die Kachel zeigt es', empfohlen: true },
    ],
  },
  {
    feld: 'titelDiffTraeger',
    frage: 'Entfernte Woerter im Titel',
    hinweis: 'Rot ist heute der alleinige Traeger. Der Zaehler ersetzt ihn, 3 rote Flaechen weniger.',
    werte: [
      { wert: 'farbe', text: 'Rote Flaeche' },
      { wert: 'zaehlerImLabel', text: 'Zaehler im Label', empfohlen: true },
    ],
  },
  {
    feld: 'insMarke',
    frage: 'Ergaenzte Woerter im Titel',
    hinweis: 'Gemessen auf dem Nachher-Kasten: gruen 4,75 hell, neutral 16,22.',
    werte: [
      { wert: 'gruen', text: 'Gruene Flaeche' },
      { wert: 'neutral', text: 'Neutral', empfohlen: true },
    ],
  },
  {
    feld: 'punkteTiefe',
    frage: 'Punkte-Spalte',
    hinweis:
      'Heute 276 px, groesser als die Kachel, die sie erklaeren soll. 215 px, 9 Farbflaechen. Eine Zeile: nur ein Aufklapper, wenn sich wirklich etwas geaendert hat.',
    werte: [
      { wert: 'immerOffen', text: 'Alle sechs offen' },
      { wert: 'aufklapperMitBefund', text: 'Gefaltet, mit Befund' },
      { wert: 'eineZeile', text: 'Eine Zeile', empfohlen: true },
    ],
  },
  {
    feld: 'feedSpalteDichte',
    frage: 'Dichte der rechten Spalte',
    hinweis: '20 bis 36 px, je nachdem wie viele Bloecke ueberhaupt erscheinen.',
    werte: [
      { wert: 'normal', text: 'Wie heute' },
      { wert: 'kompakt', text: 'Kompakt', empfohlen: true },
    ],
  },
  {
    feld: 'punkteAnzeige',
    frage: 'Gesamtpunktzahl',
    hinweis: 'Derselbe Ring wie im Feed-Check: Wiedererkennung, die Zahl als Bild. Kostet rund 40 px.',
    werte: [
      { wert: 'zahl', text: 'Nackte Zahl' },
      { wert: 'ring', text: 'Feed-Check-Ring', empfohlen: true },
    ],
  },
  {
    feld: 'chipMarke',
    frage: 'Marke an ergaenzten Chips',
    hinweis: 'Das Sparkle heisst ueberall sonst schon "das kam von der KI". Der Punkt sagt es leiser.',
    werte: [
      { wert: 'punkt', text: 'Violetter Punkt' },
      { wert: 'sparkle', text: 'Sparkle-Icon', empfohlen: true },
    ],
  },
  {
    feld: 'wasGeaendertIcon',
    frage: 'Zeichen an den Aenderungskarten',
    hinweis:
      'Mit Sparkle-Chips traegt die Karte das Sparkle doppelt. Der Haken trennt die Sprache: Sparkle = kam von der KI, Haken = Verbesserung umgesetzt.',
    werte: [
      { wert: 'sparkle', text: 'Sparkle wie heute' },
      { wert: 'haken', text: 'Gruener Haken', empfohlen: true },
    ],
  },
  {
    feld: 'wasGeaendertLayout',
    frage: 'Form der Aenderungsliste',
    hinweis:
      'Drei Zeilen statt drei Boxen: eine Rahmenebene weniger. Chips sind am dichtesten: nur der Titel sichtbar, der Begruendungssatz haengt am Tooltip und am aria-label.',
    werte: [
      { wert: 'karten', text: 'Boxen wie heute' },
      { wert: 'liste', text: 'Checkliste' },
      { wert: 'chips', text: 'Chip-Zeile', empfohlen: true },
    ],
  },
  {
    feld: 'titelAnordnung',
    frage: 'Titel-Anordnung',
    hinweis:
      'Held mit Fussnote: der neue Titel gross, darunter das Original am Stück als Fussnote. Ein Textlauf: beide als ein Diff, bei umgestellten Wörtern schwer lesbar; der Kürzungsstrich hat dort keinen Träger.',
    werte: [
      { wert: 'originalOben', text: 'Original oben' },
      { wert: 'originalFussnote', text: 'Held mit Fussnote', empfohlen: true },
      { wert: 'inline', text: 'Ein Textlauf' },
    ],
  },
  {
    feld: 'kategorieZeile',
    frage: 'Kategorie-Darstellung',
    hinweis:
      'Statt der mehrzeiligen Riesen-Pille ein gestrichelter Chip im selben Raster: nur das letzte Segment sichtbar, der volle Pfad im Tooltip und im aria-label. Fehlende Kategorie bleibt Warn-Chip.',
    werte: [
      { wert: 'pille', text: 'Chip wie heute' },
      { wert: 'zeile', text: 'Schlusszeile' },
      { wert: 'gestrichelterChip', text: 'Gestrichelter Chip', empfohlen: true },
    ],
  },
  {
    feld: 'punkteOrt',
    frage: 'Wo die Punktzahl wohnt',
    hinweis:
      'Der Score steht dreimal auf dem Schirm (Liste, linke Spalte, bald Karten). Diese Variante zieht ihn als Badge in die Aenderungsliste, Klick klappt den Breakdown auf.',
    werte: [
      { wert: 'kachel', text: 'Linke Spalte wie heute', empfohlen: true },
      { wert: 'imAenderungsblock', text: 'Badge im Aenderungsblock' },
    ],
  },
  {
    feld: 'leerzustand',
    frage: 'Leerzustand nach der Freigabe',
    hinweis: 'Alles freigegeben ist ein Erfolg. Heute steht er im Fliesstext, dann als Zahl im Bild.',
    werte: [
      { wert: 'schlicht', text: 'Wie heute' },
      { wert: 'erfolgsmoment', text: 'Erfolgsmoment', empfohlen: true },
    ],
  },
  {
    feld: 'kachelBild',
    frage: 'Produktbild in der Kachel',
    hinweis: 'Die Kachel ist der emotionale Anker der Karte. 64 px mehr Bild, die der Spalte abgehen.',
    werte: [
      { wert: 'normal', text: '128 px' },
      { wert: 'gross', text: '192 px', empfohlen: true },
    ],
  },
  {
    feld: 'highlightsEditor',
    frage: 'Highlights im Aufklapper',
    hinweis: 'Null Pixel im Ruhebild. Zahlt sich erst aus, wenn jemand aufklappt. Mit Nur-Lese-Vorstufe.',
    werte: [
      { wert: 'wieHeute', text: 'Sofort Eingabefelder' },
      { wert: 'hinterAnpassen', text: 'Liste, dann Anpassen', empfohlen: true },
    ],
  },
];

function laden(): EntwurfOptionen {
  try {
    const roh = localStorage.getItem(SPEICHER);
    if (!roh) return AUSWAHL_DENNIS;
    const gelesen = JSON.parse(roh) as Partial<EntwurfOptionen>;
    // Unbekannte oder fehlende Felder fallen auf Dennis' gepinnte Auswahl
    // zurueck, statt die Seite mit einem halben Zustand zu starten.
    return { ...AUSWAHL_DENNIS, ...gelesen };
  } catch {
    return VORSCHLAG;
  }
}

const EntwurfWerkbank: React.FC = () => {
  const [optionen, setOptionen] = useState<EntwurfOptionen>(laden);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SPEICHER, JSON.stringify(optionen));
    } catch {
      /* Speicher gesperrt: die Wahl gilt dann nur fuer diese Sitzung. */
    }
  }, [optionen]);

  const [kopiert, setKopiert] = useState(false);
  /* Zwischenablage best-effort: schlaegt sie fehl (Berechtigung, alter
     Browser), zeigt ein prompt() den Text zum Selbstkopieren - der Stand darf
     nie unerreichbar sein, er ist der ganze Zweck des Knopfs. */
  const kopieren = useCallback(() => {
    const text = exportOptionen(optionen);
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setKopiert(true);
        setTimeout(() => setKopiert(false), 2000);
      })
      .catch(() => window.prompt('Auswahl (manuell kopieren):', text));
  }, [optionen]);

  const setzen = useCallback(<K extends keyof EntwurfOptionen>(feld: K, wert: EntwurfOptionen[K]) => {
    setOptionen((v) => ({ ...v, [feld]: wert }));
  }, []);

  const istAuswahl = (Object.keys(AUSWAHL_DENNIS) as Array<keyof EntwurfOptionen>).every(
    (f) => optionen[f] === AUSWAHL_DENNIS[f],
  );
  const istVorschlag = (Object.keys(VORSCHLAG) as Array<keyof EntwurfOptionen>)
    .every((k) => optionen[k] === VORSCHLAG[k]);
  const istHeute = (Object.keys(STANDARD) as Array<keyof EntwurfOptionen>)
    .every((k) => optionen[k] === STANDARD[k]);

  return (
    <>
      <EntwurfProvider optionen={optionen}>
        <Workbench />
      </EntwurfProvider>

      {/* Schwebt ueber der Seite, siehe Dateikopf: im Fluss wuerde die Leiste
          genau die Hoehe verbrauchen, ueber die hier entschieden wird. */}
      <div className="fixed bottom-4 left-4 z-50 print:hidden">
        {offen ? (
          <div className="w-[330px] max-h-[78vh] overflow-y-auto rounded-xl border border-border bg-card shadow-lg">
            <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-3">
              <div>
                <p className="text-sm font-semibold">Entwurf</p>
                <p className="text-xs text-foreground/70">
                  {istAuswahl ? 'Deine Auswahl (24.08.)' : istVorschlag ? 'Vorschlag' : istHeute ? 'Wie heute' : 'Eigene Mischung'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOffen(false)}
                aria-label="Schalter schließen"
                className="rounded-md p-1.5 text-foreground/70 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOptionen(STANDARD)}
                  className="flex-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  Alles wie heute
                </button>
                <button
                  type="button"
                  onClick={() => setOptionen(VORSCHLAG)}
                  className="flex-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  Vorschlag
                </button>
                <button
                  type="button"
                  data-testid="profil-dennis"
                  onClick={() => setOptionen(AUSWAHL_DENNIS)}
                  className="flex-1 rounded-md bg-primary px-2 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                >
                  Deine Auswahl
                </button>
              </div>
              <button
                type="button"
                data-testid="auswahl-kopieren"
                onClick={kopieren}
                className="flex items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs font-medium hover:bg-muted"
              >
                {kopiert ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                    Kopiert
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    Auswahl kopieren
                  </>
                )}
              </button>
            </div>

            <div className="flex flex-col gap-4 px-4 py-4">
              {SCHALTER.map((s) => (
                <div key={String(s.feld)}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                    {s.frage}
                  </p>
                  {s.hinweis && (
                    <p className="mt-0.5 text-[11px] leading-snug text-foreground/70">{s.hinweis}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.werte.map((w) => {
                      const aktiv = optionen[s.feld as keyof EntwurfOptionen] === w.wert;
                      return (
                        <button
                          key={String(w.wert)}
                          type="button"
                          aria-pressed={aktiv}
                          onClick={() => setzen(s.feld as keyof EntwurfOptionen, w.wert)}
                          className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
                            aktiv
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          {w.text}
                          {w.empfohlen && !aktiv ? ' ·' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border px-4 py-3">
              <p className="text-[11px] leading-snug text-foreground/70">
                Der Punkt hinter einer Wahl ist meine Empfehlung. Diese Seite ist unverlinkt
                und ändert nichts an <span className="font-mono">/workbench</span>.
              </p>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOffen(true)}
            className="flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-medium shadow-lg hover:bg-muted"
          >
            <Settings2 className="h-4 w-4" />
            Entwurf
            <span className="text-xs text-foreground/70">
              {istAuswahl ? 'deine Auswahl' : istVorschlag ? 'Vorschlag' : istHeute ? 'wie heute' : 'gemischt'}
            </span>
          </button>
        )}
      </div>
    </>
  );
};

export default EntwurfWerkbank;
